import type { ComponentProps, ReactNode, ForwardedRef } from "react";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  FormProvider,
  Path,
  useForm,
  type UseFormReturn,
} from "react-hook-form";
import type {
  UseFormProps,
  FieldValues,
  SubmitErrorHandler,
} from "react-hook-form";
import { isPromise } from "../utils/isPromise";

export interface FormProps<TFieldValues extends FieldValues>
  extends UseFormProps<TFieldValues>,
    Omit<ComponentProps<"form">, "onSubmit" | "onError" | "children"> {
  onSubmit: (
    data: TFieldValues,
    formUtils: UseFormReturn<TFieldValues>
  ) => void | Promise<void | { errors?: Record<string, string> }>;
  onError?: SubmitErrorHandler<TFieldValues>;
  children: ReactNode;
}

type FormRef<TFieldValues extends FieldValues> = ForwardedRef<
  UseFormReturn<TFieldValues>
>;

function InnerForm<TFieldValues extends FieldValues = FieldValues>(
  {
    onSubmit,
    onError,
    mode = "onTouched",
    reValidateMode = "onChange",
    shouldUnregister = false,
    shouldFocusError = true,
    shouldUseNativeValidation = false,
    criteriaMode = "firstError",
    context,
    defaultValues,
    delayError,
    resolver,
    values,
    ...formProps
  }: FormProps<TFieldValues>,
  ref: FormRef<TFieldValues>
) {
  const methods = useForm<TFieldValues>({
    defaultValues,
    values,
    mode,
    reValidateMode,
    criteriaMode,
    delayError,
    context,
    resolver,
    shouldUnregister,
    shouldFocusError,
    shouldUseNativeValidation,
  });

  useImperativeHandle(ref, () => methods, [methods]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          methods.handleSubmit((data) => {
            const submitResult = onSubmit(data, methods);
            if (!isPromise(submitResult)) return;

            return submitResult.then((response) => {
              if (!response?.errors) return;

              Object.entries(response.errors).forEach(([field, message]) => {
                methods.setError(field as Path<TFieldValues>, {
                  type: "server",
                  message: message as string,
                });
              });
            });
          }, onError)(e);
        }}
        noValidate
        {...formProps}
      />
    </FormProvider>
  );
}
export const Form = forwardRef(InnerForm) as <TFieldValues extends FieldValues>(
  props: FormProps<TFieldValues> & { ref?: FormRef<TFieldValues> }
) => JSX.Element;

export const useFormRef = <TFieldValues extends FieldValues = FieldValues>() =>
  useRef<UseFormReturn<TFieldValues>>(null);
